/**
 * Поиск по адресам, улицам и кадастровым номерам.
 *
 * Индекс грузится один раз и целиком остаётся в памяти вкладки. Это сознательный
 * размен: пара мегабайт оперативной памяти в обмен на мгновенный отклик и на
 * работоспособность поиска в офлайне, где обращаться было бы просто некуда.
 *
 * Загрузка ленивая — до первого нажатия на поле поиска ни одного байта индекса
 * не скачивается, поэтому первая отрисовка карты не замедляется.
 */

export type SearchKind = "place" | "street" | "address" | "cadastre" | "object";

export interface SearchEntry {
  /** Название или кадастровый номер. */
  n: string;
  k: SearchKind;
  /** [lon, lat] */
  c: [number, number];
  /** Слой генплана, если запись пришла оттуда. */
  l?: string;
  /** Код функциональной зоны, если известен. */
  z?: string;
}

export interface SearchResult extends SearchEntry {
  score: number;
}

const INDEX_URL = "/search/index.json";

/** Чем меньше число, тем выше тип в выдаче при равном совпадении. */
const KIND_RANK: Record<SearchKind, number> = {
  place: 0,
  street: 1,
  address: 2,
  cadastre: 3,
  object: 4,
};

export const KIND_LABELS: Record<SearchKind, string> = {
  place: "Населённый пункт",
  street: "Улица",
  address: "Адрес",
  cadastre: "Кадастровый номер",
  object: "Объект",
};

/**
 * «Ё» и «е» люди набирают как придётся, а дефисы и точки в кадастровых
 * номерах ставят по-разному. Нормализация убирает оба источника промахов.
 */
function normalize(value: string): string {
  return value.toLowerCase().replace(/ё/g, "е").replace(/[^\wа-я0-9:]+/gi, " ").trim();
}

export class SearchIndex {
  private entries: SearchEntry[] = [];
  private normalized: string[] = [];
  private loading: Promise<void> | null = null;
  private failed = false;

  get size(): number {
    return this.entries.length;
  }

  get unavailable(): boolean {
    return this.failed;
  }

  /** Повторные вызовы разделяют один и тот же запрос. */
  async load(): Promise<void> {
    if (this.entries.length > 0 || this.failed) return;
    this.loading ??= this.fetchIndex();
    await this.loading;
  }

  private async fetchIndex(): Promise<void> {
    try {
      const response = await fetch(INDEX_URL);
      if (!response.ok) {
        this.failed = true;
        return;
      }
      const data = (await response.json()) as SearchEntry[];
      this.entries = data;
      this.normalized = data.map((entry) => normalize(entry.n));
    } catch {
      this.failed = true;
    }
  }

  search(query: string, limit = 12): SearchResult[] {
    const needle = normalize(query);
    if (needle.length < 2) return [];

    const results: SearchResult[] = [];
    let exactHits = 0;

    for (let index = 0; index < this.entries.length; index += 1) {
      const haystack = this.normalized[index];
      if (haystack === undefined) continue;

      const position = haystack.indexOf(needle);
      if (position === -1) continue;

      const entry = this.entries[index];
      if (!entry) continue;

      // Точное совпадение важнее совпадения с начала, а оно — важнее вхождения
      // где-то в середине. Длина строки разводит «Садовая» и «Садовая роща».
      let score = position === 0 ? (haystack.length === needle.length ? 0 : 10) : 40 + position;
      score += KIND_RANK[entry.k] * 2;
      score += Math.min(20, haystack.length - needle.length) / 10;

      results.push({ ...entry, score });

      // Прерываться «набрав достаточно» нельзя по количеству: индекс
      // отсортирован по типу записи, а типы идут по алфавиту — address раньше
      // street и place. Запрос «Ленинский» набрал бы пятьсот домов на
      // Ленинском проспекте и оборвался, так и не дойдя до самой улицы, ради
      // которой и придуман KIND_RANK.
      //
      // Поэтому останавливаемся только когда набрано достаточно ТОЧНЫХ
      // совпадений: они всё равно окажутся выше любого последующего вхождения.
      if (score <= 10) {
        exactHits += 1;
        if (exactHits >= limit * 3) break;
      }
    }

    results.sort((a, b) => a.score - b.score || a.n.localeCompare(b.n, "ru"));
    return results.slice(0, limit);
  }
}
